/**
 * What this device is keeping offline, as the interface sees it.
 *
 * A thin layer over `@transport`'s retention capability: signals the UI can
 * render from, and the four things somebody can do. The policy is in Rust —
 * how large a cap is, what order the archive fills in, which body is dropped
 * next — and deliberately not duplicated here. What this file owns is when to
 * ask and what to do with the answer.
 *
 * # Everything begins with "is there any"
 *
 * `available` is false in a browser, where there is no local store, and every
 * control in the interface is *absent* rather than disabled when it is. That is
 * the one rule worth stating twice: a pin button that silently does nothing is
 * worse than no pin button, because a pin is a promise somebody relies on in a
 * tunnel and a disabled-looking control still reads as "this app can do that".
 *
 * The capability is `null` rather than an object answering zero, so "no
 * retention on this build" cannot be confused with "retention, holding
 * nothing" — the second is a state somebody can act on and the first is a pane
 * that should not exist.
 */

import { computed, signal } from "@preact/signals";
import { retention as control } from "@transport";

import type { RetentionStatus } from "./net.types";
import * as store from "./store";

/** Whether this build keeps mail offline at all. Read before drawing anything. */
export const available = control !== null;

/**
 * Each account's figures, keyed by account id.
 *
 * Absent until something asks. The settings pane asks when it opens, which is
 * the only place these are drawn — polling for a number nobody is looking at
 * would walk a blob tree on a timer for no reader.
 */
export const status = signal<Map<string, RetentionStatus>>(new Map());

/**
 * The pinned messages of each account, as `accountId:emailId` keys.
 *
 * One flat set rather than a map of sets because that is the shape every reader
 * wants: `store.keyOf(row)` already produces exactly this key, so a row can ask
 * whether it is pinned without knowing which account it belongs to.
 */
export const pinned = signal<Set<string>>(new Set());

/** Messages a pin or unpin is in flight for, so the control can say so. */
export const working = signal<Set<string>>(new Set());

export const pinnedCount = computed(() => pinned.value.size);

export function isPinned(key: string): boolean {
  return pinned.value.has(key);
}

export function isWorking(key: string): boolean {
  return working.value.has(key);
}

/**
 * Load the pinned set for every account the session knows about.
 *
 * Called once when the app has a session, and again after anything that could
 * have changed it from underneath — a resync, which can destroy a message and
 * take its pin with it. Silent on failure: a pin state that could not be read
 * renders as unpinned, which is the safe direction, because the alternative is
 * claiming a message is kept when nothing has been asked.
 */
export async function load(): Promise<void> {
  if (!control) return;
  const next = new Set<string>();
  for (const account of store.accounts.peek()) {
    try {
      for (const id of await control.pinned(account.id)) next.add(`${account.id}:${id}`);
    } catch {
      // Left out of the set rather than aborting the rest: one account that
      // cannot answer must not hide another account's pins.
    }
  }
  pinned.value = next;
}

/**
 * Keep this message offline, or stop.
 *
 * The optimistic pattern the rest of the app uses is deliberately *not* used
 * here, and the difference is the point. `lib/actions.ts` patches locally and
 * rolls back because the failure it hides is a few milliseconds of local write.
 * Pinning downloads a message body over the network, which can take a moment
 * and can genuinely fail — and showing "kept offline" before the bytes are here
 * would be exactly the false promise this feature exists to avoid. So the
 * control says it is working, and only claims the pin once Rust says the body
 * has landed.
 */
export async function setPinned(
  accountId: string,
  emailId: string,
  wanted: boolean,
): Promise<void> {
  if (!control) return;
  const key = `${accountId}:${emailId}`;
  if (working.peek().has(key)) return;

  working.value = new Set(working.peek()).add(key);
  try {
    if (wanted) await control.pin(accountId, emailId);
    else await control.unpin(accountId, emailId);

    const next = new Set(pinned.peek());
    if (wanted) next.add(key);
    else next.delete(key);
    pinned.value = next;

    // The figures moved: a pin downloads a body, an unpin makes one evictable.
    void refresh(accountId);
  } catch (e) {
    store.toast(
      wanted
        ? `Could not save that message — ${message(e)}`
        : `Could not remove that saved message — ${message(e)}`,
      "error",
    );
  } finally {
    const next = new Set(working.peek());
    next.delete(key);
    working.value = next;
  }
}

/** Re-read one account's figures. */
export async function refresh(accountId: string): Promise<void> {
  if (!control) return;
  try {
    const got = await control.status(accountId);
    status.value = new Map(status.peek()).set(accountId, got);
  } catch {
    // A figure that cannot be read is left as it was. The pane shows the last
    // number it had rather than a zero, which would read as "nothing is kept".
  }
}

/** Re-read every account's figures, for the pane opening. */
export async function refreshAll(): Promise<void> {
  if (!control) return;
  await Promise.all(store.accounts.peek().map((a) => refresh(a.id)));
}

/**
 * Change an account's cap and apply it immediately.
 *
 * `null` restores the build's default; `0` means keep nothing. They are
 * different answers and the caller must keep them apart — see the transport.
 */
export async function setCap(accountId: string, bytes: number | null): Promise<void> {
  if (!control) return;
  try {
    const got = await control.setCap(accountId, bytes);
    status.value = new Map(status.peek()).set(accountId, got);
    // Lowering a cap evicts, and eviction can take a pinned message's
    // neighbours without taking the pin, so the set is unchanged -- but the
    // count on screen came from `status` and has just moved.
  } catch (e) {
    store.toast(`Could not change how much is saved — ${message(e)}`, "error");
  }
}

/**
 * The ages offered, in days, with `AGE_UNLIMITED` last.
 *
 * Days rather than "3 months" because the unit is what the store keeps and a
 * translation layer between the two is a place for them to disagree. The list
 * is short on purpose: this is a question about a phone's battery, not a slider
 * somebody should tune.
 */
export const AGE_UNLIMITED = -1;

export const AGE_CHOICES: [number, string][] = [
  [7, "1 week"],
  [30, "30 days"],
  [90, "3 months"],
  [365, "1 year"],
  [AGE_UNLIMITED, "No limit"],
];

/**
 * How far back this account fetches bodies.
 *
 * `null` restores the default; {@link AGE_UNLIMITED} means only the size cap
 * binds. Different answers, kept apart for the reason the transport gives.
 *
 * Note this does *not* evict, unlike {@link setCap} — it changes what the next
 * sync fetches. Shortening the window and watching a screen report the same
 * usage is correct rather than broken, and the screen should not imply
 * otherwise.
 */
export async function setAge(accountId: string, days: number | null): Promise<void> {
  if (!control) return;
  try {
    const got = await control.setAge(accountId, days);
    status.value = new Map(status.peek()).set(accountId, got);
  } catch (e) {
    store.toast(`Could not change how far back mail is saved — ${message(e)}`, "error");
  }
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
